// firmwareParse.ts — client-side firmware introspection.
//
// Two jobs, both run when a file is dropped into the upload modal (or the
// drop-to-find bar):
//   1. clientSha256(file)   — display-only digest; the server recomputes it.
//   2. autoDetectMeta(file) — pull git hash / version / architecture out of the
//      file so the engineer barely has to type. A `.px4` is a JSON envelope, so
//      we read it directly (mirrors the server's extractPx4Metadata); for any
//      file we also sniff the name for an architecture preset (the WiFi uBlox
//      build, for instance, has no envelope but a recognizable name).
//
// Everything is best-effort: a non-px4 or unparseable file just yields {}.

import { CATEGORY_SLUGS } from './types';
import type { ArchPreset, FirmwareCategory } from './types';

export interface DetectedMeta {
  gitHash?: string;
  version?: string;
  architecture?: string;
  branch?: string;
  releaseDate?: string; // YYYY-MM-DD — only a bundle's manifest carries a build date
  name?: string;        // manifest `name` — seeds the drone_vision label
}

// SHA-256 hex of the file's bytes (Web Crypto). Display only.
export async function clientSha256(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// Name-based architecture hints, keyed by preset token. Deliberately broader
// than the short token: the source filename usually carries the *long* form
// ("ublox", "fmu-v6x") rather than our abbreviation ("ubx", "ff").
// KEEP IN SYNC with the server's ARCH_PRESETS tokens — adding a 4th preset there
// means adding its name hint here, or name-detection silently won't cover it.
const ARCH_NAME_HINTS: Record<string, RegExp> = {
  ark: /ark[_\-]?pi6x|arkpi6x|(^|[_.\-])ark([_.\-]|$)/i,
  ff: /firefly[_\-]?fmu|fmu-?v6x|(^|[_.\-])ff([_.\-]|$)/i,
  ubx: /u-?blox|ubx|wifi/i,
  aarch64: /aarch64|arm64/i,
};

// Map .px4 board hints (magic / summary / description) to a preset token.
function archTokenFromBoardHint(hint: string): string | null {
  const h = hint.toLowerCase();
  if (/arkpi6x|ark[_\-]?pi6x/.test(h)) return 'ark';
  if (/fmu-?v6x|firefly[_\-]?fmu/.test(h)) return 'ff';
  if (/u-?blox|ublox/.test(h)) return 'ubx';
  return null;
}

const presetArch = (presets: ArchPreset[], token: string | null): string | undefined =>
  token ? presets.find((p) => p.token === token)?.arch : undefined;

// Generous parse ceiling — firmware images are ~2MB; skip larger files so we
// don't lock the tab on a synchronous parse (mirrors the server guard).
const FIRMWARE_PARSE_MAX = 16 * 1024 * 1024;

// Branch shown for a build sitting exactly on a tag (detached HEAD — no branch).
// Mirrors firmware-service.TAG_BUILD_BRANCH.
export const TAG_BUILD_BRANCH = '[RELEASE]';

// Is `s` plausibly a git branch (vs. a version, a hash, or a source path)?
function isBranchLike(s: string): boolean {
  if (!s || s.length > 100) return false;
  if (/^[-.]/.test(s)) return false; // git refnames can't start with - or . (kills the "-dirty" suffix)
  if (/^(localhost|HEAD)$/i.test(s)) return false; // ver_oem default / detached-HEAD marker, not a branch
  if (!/[A-Za-z]/.test(s)) return false;
  if (/^v?\d+(?:\.\d+)+/.test(s)) return false;
  if (/^[0-9a-f]{40}$/i.test(s)) return false;
  if (s.startsWith('/') || s.includes('PX4-Autopilot') || /\.(c|cpp|h|hpp)$/i.test(s)) return false;
  return /^[\w./-]+$/.test(s);
}

// Inflate a base64 zlib-deflate blob (the .px4 `image` field) to bytes via the
// native Streams API — works in the browser pre-upload, no library. Null on any
// failure (unsupported / corrupt / too large).
async function inflateBase64(b64: string): Promise<Uint8Array | null> {
  try {
    const bin = atob(b64);
    const raw = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) raw[i] = bin.charCodeAt(i);
    const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

// Raw-binary (.bin/.elf) scan — mirrors firmware-service.scanBinaryVersion.
// Handles both "git describe" builds (v1001.15.4-3-gc227219674) and tag-exact
// builds (just v1001.15.4, no -g, no branch). The compiler marker "GNU GCC" is the
// reliable anchor: the PX4 hash is the first 40-hex after it; the version + branch
// sit just before. The describe, when present, refines the full hash + branch.
function scanBinaryVersion(text: string): DetectedMeta {
  let gitHash: string | undefined;
  let version: string | undefined;
  let branch: string | undefined;

  const gccIdx = text.lastIndexOf('GNU GCC'); // version block lives near the tail
  if (gccIdx >= 0) {
    const before = text.slice(Math.max(0, gccIdx - 300), gccIdx);
    const after = text.slice(gccIdx, gccIdx + 160);
    const h = /[0-9a-f]{40}/i.exec(after); // PX4 hash: first 40-hex after the compiler version
    if (h) gitHash = h[0].toLowerCase();
    const vers = before.match(/v\d+(?:\.\d+){1,3}(?:-\d+-g[0-9a-f]{7,40})?(?:-dirty)?/gi) || [];
    if (vers.length) version = vers[0]; // first v-version = PX4; second = OS/vendor
    const sV = version ? before.indexOf(version) + version.length : 0;
    // Bracket by the next DISTINCT version (a byte-identical vendor version would
    // re-find the same token and mis-window) — else fall back to the whole tail.
    const hasDistinctVendor = vers.length > 1 && vers[1] !== version;
    const eV = hasDistinctVendor ? before.indexOf(vers[1], sV) : before.length;
    const slice = before.slice(sV, eV < 0 ? before.length : eV);
    for (const t of slice.split('\x00').slice(0, 6)) {
      const cand = t.trim();
      if (cand && isBranchLike(cand)) { branch = cand.replace(/\//g, '-'); break; }
    }
  }

  const desc = /v?\d+(?:\.\d+){1,3}-\d+-g([0-9a-f]{7,40})/i.exec(text);
  if (desc) {
    version = version || desc[0];
    const shortHash = desc[1].toLowerCase();
    const full = new RegExp(`\\b${shortHash}[0-9a-f]{${Math.max(0, 40 - shortHash.length)},}\\b`, 'i').exec(text);
    gitHash = (full && full[0].length >= 40) ? full[0].toLowerCase() : (gitHash || shortHash);
    if (!branch) {
      const tokens = text.slice(desc.index + desc[0].length, desc.index + desc[0].length + 600).replace(/^\x00+/, '').split('\x00');
      for (const t of tokens.slice(0, 8)) {
        const cand = t.trim();
        if (cand && isBranchLike(cand)) { branch = cand.replace(/\//g, '-'); break; }
      }
    }
  }

  // Tag-exact build (no "-N-g<hash>" describe component) has no branch — label it
  // rather than leaving it blank (describe builds keep a blank = "couldn't parse").
  if (!branch && version && /^v?\d+(?:\.\d+)+(?:-dirty)?$/i.test(version)) {
    branch = TAG_BUILD_BRANCH;
  }

  return { gitHash, version, branch };
}

// Extract git hash / version / branch / architecture from the file itself —
// the `.px4` JSON envelope (root metadata + board hint) or, failing that, a raw
// binary's embedded version strings. Mirrors firmware-service.extractFirmwareMetadata.
async function extractFromFile(file: File, presets: ArchPreset[]): Promise<DetectedMeta> {
  if (file.size > FIRMWARE_PARSE_MAX) return {};
  // latin1 decode: 1 byte -> 1 char (ASCII + NUL preserved); serves both the
  // ASCII JSON envelope and the raw-binary string scan from a single read.
  const text = new TextDecoder('latin1').decode(new Uint8Array(await file.arrayBuffer()));

  // 1) JSON envelope (.px4)
  try {
    const obj = JSON.parse(text);
    if (obj && typeof obj === 'object' && (typeof obj.git_hash === 'string' || typeof obj.git_identity === 'string')) {
      let gitHash: string | undefined = typeof obj.git_hash === 'string' ? obj.git_hash.trim() : undefined;
      const identity: string | undefined = typeof obj.git_identity === 'string' ? obj.git_identity.trim() : undefined;
      if (!gitHash && identity) {
        const m = /-g([0-9a-f]{7,40})$/i.exec(identity);
        if (m) gitHash = m[1];
      }
      const version = identity || (obj.version != null ? String(obj.version).trim() : undefined);
      const hint = `${obj.magic || ''} ${obj.summary || ''} ${obj.description || ''}`;
      const architecture = presetArch(presets, archTokenFromBoardHint(hint));

      // Branch isn't a JSON field — it's inside the compressed firmware image.
      let branch: string | undefined;
      if (typeof obj.image === 'string' && obj.image) {
        const image = await inflateBase64(obj.image);
        if (image) branch = scanBinaryVersion(new TextDecoder('latin1').decode(image)).branch;
      }
      return { gitHash: gitHash || undefined, version: version || undefined, architecture, branch };
    }
  } catch { /* not JSON — raw-binary scan */ }

  // 2) Raw binary
  return scanBinaryVersion(text);
}

// Name-only detection (any category): architecture preset + a version like
// "...-v0.1.13" (the uBlox WiFi build pattern the engineer called out).
function detectFromName(name: string, presets: ArchPreset[]): DetectedMeta {
  const meta: DetectedMeta = {};
  for (const p of presets) {
    const re = ARCH_NAME_HINTS[p.token];
    if (re && re.test(name)) {
      meta.architecture = p.arch;
      break;
    }
  }
  // Require an explicit `v` so a stray "rev2.0"/"part3.1" in a name isn't read as
  // a version (the WiFi uBlox build is named "...-v0.1.13"). Server has no
  // name-based version detection, so keep this conservative to limit drift.
  const vm = /[_.\-]v(\d+\.\d+(?:\.\d+)?)/i.exec(name);
  if (vm) meta.version = vm[1];
  return meta;
}

// ── filename preview ─────────────────────────────────────────────────────────
// Mirrors the server's deriveFileName so the modal can show the canonical name
// before upload. ADVISORY only — the server derives the authoritative name (and
// owns dup-checking). Kept in lockstep with firmware-service.deriveFileName.

const ALLOWED_EXT = ['.bin', '.px4', '.hex', '.elf', '.tar.gz', '.tgz'];
// Every dot escaped ('.tar.gz' has two) and longest-first, mirroring the server.
const EXT_STRIP_RE = new RegExp(
  `(${[...ALLOWED_EXT].sort((a, b) => b.length - a.length).map((e) => e.replace(/\./g, '\\.')).join('|')})+$`,
  'i',
);

// path-extname equivalent, two-part-extension aware — '.tar.gz' must not read
// as '.gz'. Mirrors firmware-service.firmwareExt.
export function firmwareExt(name: string | null | undefined): string {
  const n = String(name || '').toLowerCase();
  if (n.endsWith('.tar.gz')) return '.tar.gz';
  const m = /\.[^.]+$/.exec(n);
  return m ? m[0] : '';
}

const sanitizeToken = (s: string): string =>
  String(s || '').replace(/[^\w.\-]+/g, '_').replace(/_{2,}/g, '_').replace(/^_+|_+$/g, '');
const stripExt = (s: string): string => String(s || '').replace(EXT_STRIP_RE, '');
const slashToDash = (s: string): string => String(s || '').replace(/\//g, '-'); // branch "a/b" -> "a-b"

function archToken(architecture: string, presets: ArchPreset[]): string {
  const a = (architecture || '').trim();
  if (!a) return '';
  const p = presets.find((x) => x.arch.toLowerCase() === a.toLowerCase());
  if (p) return p.token;
  return (sanitizeToken(a).split('_')[0] || '').toLowerCase().slice(0, 12);
}

const toYYMMDD = (ymd: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '');
  return m ? `${m[1].slice(2)}${m[2]}${m[3]}` : '';
};

const todayYMD = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function buildFileName(opts: {
  category: string;
  architecture: string;
  releaseDate: string; // YYYY-MM-DD
  branch: string;
  label: string;
  gitHash: string;
  sha256: string;
  ext: string;
  presets: ArchPreset[];
}): string {
  const shortHash = opts.gitHash.trim() ? opts.gitHash.trim().slice(0, 7) : (opts.sha256 || '').slice(0, 7);
  const base = [
    CATEGORY_SLUGS[opts.category as FirmwareCategory] || opts.category, // mirrors the server: px4/wifi/lora are their own slug
    archToken(opts.architecture, opts.presets),
    toYYMMDD(opts.releaseDate || todayYMD()), // mirror the server: blank -> today

    opts.branch ? sanitizeToken(stripExt(slashToDash(opts.branch))) : '',
    opts.label ? sanitizeToken(stripExt(slashToDash(opts.label))) : '',
    shortHash ? sanitizeToken(shortHash) : '',
  ]
    .filter(Boolean)
    .join('_');
  return `${stripExt(base)}${opts.ext}`;
}

// ── drone_vision bundle introspection ────────────────────────────────────────
// Browser twin of firmware-service.extractBundleManifest: gunzip the bundle as a
// stream (DecompressionStream — no library), walk the 512-byte tar headers, and
// keep only manifest.json's bytes. The manifest is the LAST member of a ~40 MB
// decompressed stream, so this never holds more than one chunk plus the manifest;
// the 16 MB extractFromFile ceiling deliberately does not apply.
const BUNDLE_MEMBER_MAX = 1024 * 1024;
const BUNDLE_SCAN_MAX = 512 * 1024 * 1024; // decompressed-walk ceiling, mirrors the server

async function extractBundleManifest(file: File): Promise<Record<string, unknown> | null> {
  try {
    const stream = file.stream().pipeThrough(new DecompressionStream('gzip'));
    const reader = stream.getReader();
    let pending = new Uint8Array(0);
    let toSkip = 0; // member content + padding still to discard
    let manifestWant = 0; // manifest bytes still to collect (>0 = collecting)
    let manifestParts: Uint8Array[] = [];
    let walked = 0;

    const concat = (a: Uint8Array, b: Uint8Array): Uint8Array => {
      if (!a.length) return b;
      const out = new Uint8Array(a.length + b.length);
      out.set(a); out.set(b, a.length);
      return out;
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) return null; // stream ended before the manifest — not a bundle we understand
      walked += value.length;
      if (walked > BUNDLE_SCAN_MAX) { reader.cancel().catch(() => {}); return null; }
      pending = concat(pending, value);

      for (;;) {
        if (toSkip > 0) {
          const n = Math.min(toSkip, pending.length);
          pending = pending.subarray(n);
          toSkip -= n;
          if (toSkip > 0) break; // need more input
        }
        if (manifestWant > 0) {
          const n = Math.min(manifestWant, pending.length);
          manifestParts.push(pending.subarray(0, n));
          pending = pending.subarray(n);
          manifestWant -= n;
          if (manifestWant > 0) break;
          reader.cancel().catch(() => {});
          let total = 0;
          for (const part of manifestParts) total += part.length;
          const bytes = new Uint8Array(total);
          let off = 0;
          for (const part of manifestParts) { bytes.set(part, off); off += part.length; }
          return JSON.parse(new TextDecoder().decode(bytes));
        }
        if (pending.length < 512) break; // need a full header
        const header = pending.subarray(0, 512);
        pending = pending.subarray(512);
        if (header.every((b) => b === 0)) { reader.cancel().catch(() => {}); return null; } // end-of-archive
        const dec = new TextDecoder();
        const size = parseInt(dec.decode(header.subarray(124, 136)).replace(/\0.*$/, ''), 8) || 0;
        const typeflag = header[156];
        const isFile = typeflag === 0x30 || typeflag === 0; // '0' or the pre-POSIX NUL
        const name = dec.decode(header.subarray(0, 100)).replace(/\0.*$/, '').replace(/^(\.\/|\/)+/, '');
        if (isFile && name === 'manifest.json') {
          if (size <= 0 || size > BUNDLE_MEMBER_MAX) { reader.cancel().catch(() => {}); return null; }
          manifestWant = size;
          manifestParts = [];
        } else {
          toSkip = Math.ceil(size / 512) * 512;
        }
      }
    }
  } catch {
    return null;
  }
}

// manifest.json -> DetectedMeta. Mirrors firmware-service.extractBundleMetadata.
async function extractBundleMeta(file: File): Promise<DetectedMeta> {
  const m = await extractBundleManifest(file);
  if (!m || typeof m !== 'object') return {};
  const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v.trim() ? v.trim() : undefined;
  return {
    gitHash: str(m.git_sha),
    version: m.version != null ? str(String(m.version)) : undefined,
    architecture: str(m.abi),
    releaseDate: /^\d{4}-\d{2}-\d{2}/.test(String(m.built_at || '')) ? String(m.built_at).slice(0, 10) : undefined,
    name: str(m.name),
  };
}

/**
 * Best-effort auto-fill for the upload form. `.px4` envelope wins (it's ground
 * truth); name-based detection fills only what the envelope didn't. A
 * drone_vision bundle (.tar.gz/.tgz) reads its manifest.json instead.
 */
export async function autoDetectMeta(file: File, presets: ArchPreset[]): Promise<DetectedMeta> {
  const fromName = detectFromName(file.name, presets);
  const ext = firmwareExt(file.name);
  const fromFile = ext === '.tar.gz' || ext === '.tgz'
    ? await extractBundleMeta(file) // bundle manifest
    : await extractFromFile(file, presets); // .px4 envelope OR raw-binary scan
  // File-derived values take precedence; name detection backfills the gaps.
  return {
    gitHash: fromFile.gitHash || fromName.gitHash,
    version: fromFile.version || fromName.version,
    architecture: fromFile.architecture || fromName.architecture,
    branch: fromFile.branch || fromName.branch,
    releaseDate: fromFile.releaseDate,
    name: fromFile.name,
  };
}
